import { getFormErrorMessage } from "@core/utility/get-error-message";
import { RoomTypeFormRule, RoomTypeFormSchema } from "@factory/form/form.rule";
import { useFormDefaultRoomType } from "@factory/form/form-default-room-type.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseRoomTypeModel } from "@factory/model/query-model-room-type";
import { 
  useQyDeleteRoomType, 
  useQyGetRoomTypeById, 
  useQyUpdateRoomType} from "@factory/query/room-type.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditRoomType() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultRoomType();
  const navigate = useNavigate();
  const { mapUpdateRoomType, mapDeleteRoomType } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { roomTypeId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID RoomType
  const handleGetApiSuccess = (data: GetResponseRoomTypeModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("hotel", responseData?.hotel || "");
      setValue("validFrom", responseData?.valid_from || "");
      setValue("validUntil", responseData?.valid_until || "");
      setValue("name", responseData?.name || "");
      setValue("price", responseData?.price);
  };
  const {
    data: roomTypeResponse,
    isLoading,
    isError: roomTypeError,
  } = useQyGetRoomTypeById(roomTypeId || "", handleGetApiSuccess);
  const roomTypeData = roomTypeResponse?.data?.[0];

  // API UPDATE RoomType
  const handleUpdateApiSuccess = () => {
    showSuccess("RoomType updated successfully");
  };
  const { mutate: updateRoomType, isLoading: isEditLoading } = useQyUpdateRoomType(
    handleUpdateApiSuccess
  );

  // API DELETE RoomType
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("RoomType deleted successfully");
  };
  const { mutate: deleteRoomType } = useQyDeleteRoomType(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<RoomTypeFormSchema>({
    defaultValues: getDefault(roomTypeData),
    resolver: zodResolver(RoomTypeFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: RoomTypeFormSchema) => {
    if (!roomTypeData || !roomTypeData.code) {
      throw new Error("No roomType data");
    }

    const formData = mapUpdateRoomType(form, roomTypeData.code);
    updateRoomType(formData);
  };
  const handleValidateError = (err: FieldErrors<RoomTypeFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!roomTypeData || !roomTypeData.code) {
      throw new Error("No roomType data found");
    }

    const form = mapDeleteRoomType(roomTypeData.code);
    deleteRoomType(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    roomTypeId,
    roomTypeData,
    isLoading,
    roomTypeError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}